import '../../card/card.css';
import '../../submission/submission.css';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import BannerImage from '../../assets/Space.jpg';
import './cracked.css';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const parseGradeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getGradeChange = (oldGrade, newGrade) => {
  const oldValue = parseGradeNumber(oldGrade);
  const newValue = parseGradeNumber(newGrade);

  if (oldValue === null || newValue === null) {
    return 'Unknown';
  }

  if (newValue > oldValue) {
    return 'Upgrade';
  }

  if (newValue < oldValue) {
    return 'Downgrade';
  }

  return 'Same';
};

const getGradeDelta = (oldGrade, newGrade) => {
  const oldValue = parseGradeNumber(oldGrade);
  const newValue = parseGradeNumber(newGrade);

  if (oldValue === null || newValue === null) {
    return '-';
  }

  const delta = newValue - oldValue;
  if (delta === 0) {
    return '0';
  }

  return `${delta > 0 ? '+' : ''}${delta}`;
};

const resolveImageUrl = (rawValue) => {
  if (!rawValue || rawValue === 'none' || rawValue === 'undefined') {
    return 'https://alientrader.net/cards/cards/images/cards/Temp.jpg';
  }

  try {
    const parsed = JSON.parse(rawValue);
    const thumbnail = parsed?.[0]?.thumbnail;

    if (!thumbnail || thumbnail === 'none') {
      return 'https://alientrader.net/cards/cards/images/cards/Temp.jpg';
    }

    if (thumbnail.includes('/home/u825454141/domains/alientrader.net/public_html/')) {
      return thumbnail.replace(
        '/home/u825454141/domains/alientrader.net/public_html/',
        'https://alientrader.net/'
      );
    }

    return `https://api.alientrader.cards/${thumbnail}`;
  } catch (error) {
    return 'https://alientrader.net/cards/cards/images/cards/Temp.jpg';
  }
};

const getChangeClassName = (changeType) => {
  if (changeType === 'Upgrade') return 'cracked-change-upgrade';
  if (changeType === 'Downgrade') return 'cracked-change-downgrade';
  if (changeType === 'Same') return 'cracked-change-same';
  return 'cracked-change-unknown';
};

function PairCard({ item }) {
  const changeType = getGradeChange(item.old_card_grade_number, item.new_card_grade_number);
  const gradeDelta = getGradeDelta(item.old_card_grade_number, item.new_card_grade_number);

  return (
    <div className="cracked-pair-card shadow-sm">
      <div className="cracked-pair-header">
        <div className="cracked-pair-heading">
          <span className="cracked-pair-title">{item.new_card_pokemon || item.old_card_pokemon || 'Cracked Pair'}</span>
          <span className="cracked-pair-subtitle">
            {item.new_card_set_year} {item.new_card_set}
          </span>
        </div>
        <div className={`cracked-change-pill ${getChangeClassName(changeType)}`}>
          {changeType} {gradeDelta !== '-' ? `(${gradeDelta})` : ''}
        </div>
      </div>

      <div className="cracked-pair-visual">
        <a className="cracked-side" href={item.old_card_id ? `/card/${item.old_card_id}` : undefined}>
          <div className="cracked-side-label">Original</div>
          <img
            src={resolveImageUrl(item.old_cover_image)}
            alt="original card"
            className="cracked-side-image"
          />
          <div className="cracked-side-grade">{item.old_card_grader || '-'} {item.old_card_grade_number || '-'}</div>
          <div className="cracked-side-meta">ID #{item.old_card_id || '-'}</div>
          <div className="cracked-side-meta">Cert {item.old_card_grade_cert || '-'}</div>
        </a>

        <div className="cracked-pair-arrow">
          <span className="cracked-pair-arrow-symbol">{'>'}</span>
        </div>

        <a className="cracked-side" href={item.new_card_id ? `/card/${item.new_card_id}` : undefined}>
          <div className="cracked-side-label cracked-side-label-new">New Grade</div>
          <img
            src={resolveImageUrl(item.new_cover_image)}
            alt="new grade card"
            className="cracked-side-image"
          />
          <div className="cracked-side-grade">{item.new_card_grader || '-'} {item.new_card_grade_number || '-'}</div>
          <div className="cracked-side-meta">ID #{item.new_card_id || '-'}</div>
          <div className="cracked-side-meta">Cert {item.new_card_grade_cert || '-'}</div>
        </a>
      </div>

    </div>
  );
}

function CrackedCardsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [changeFilter, setChangeFilter] = useState('All');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    const fetchRows = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://api.alientrader.cards/data/cracked', {
          params: {
            q: debouncedSearchQuery || undefined,
          },
        });

        setRows(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching cracked card pairs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, [debouncedSearchQuery]);

  const stats = useMemo(() => {
    return rows.reduce(
      (accumulator, row) => {
        const changeType = getGradeChange(row.old_card_grade_number, row.new_card_grade_number);
        accumulator.total += 1;

        if (changeType === 'Upgrade') {
          accumulator.upgrades += 1;
        } else if (changeType === 'Downgrade') {
          accumulator.downgrades += 1;
        } else if (changeType === 'Same') {
          accumulator.same += 1;
        } else {
          accumulator.unknown += 1;
        }

        return accumulator;
      },
      { total: 0, upgrades: 0, downgrades: 0, same: 0, unknown: 0 }
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (changeFilter === 'All') {
      return rows;
    }

    return rows.filter((row) => getGradeChange(row.old_card_grade_number, row.new_card_grade_number) === changeFilter);
  }, [changeFilter, rows]);

  const toggleFilter = (nextFilter) => {
    setChangeFilter((currentFilter) => (currentFilter === nextFilter ? 'All' : nextFilter));
  };

  return (
    <div className="imagesPage cracked-tag-wall">
      <div
        className="subTopBG"
        style={{
          backgroundImage: `url(${BannerImage})`,
          backgroundPosition: 'center -40px',
        }}
      >
        <div className="actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="subBack"></div>
          <div className="sub" style={{ color: '#f4f7fb' }}></div>
          <div className="subEdit" style={{ width: 'auto', marginRight: '0' }}></div>
        </div>
        <div className="subHeader">Cracked Cards</div>
      </div>

      <div className="subPositionAdjust" style={{ top: '-130px' }}>
        <Container fluid style={{ paddingLeft: '30px', paddingRight: '30px' }}>
          <Row className="d-flex justify-content-center" style={{ marginTop: '28px' }}>
            <Col xxl={10} xl={11} lg={12} md={12} className="custom-info-box shadow-sm cracked-wall-box">
              <Row>
                <div className="custom-info-header">Cracked Cards</div>
              </Row>
              <Row>
                <div className="custom-info-misc cracked-summary-text">
                  <button
                    type="button"
                    className={`cracked-summary-chip ${changeFilter === 'All' ? 'active' : ''}`}
                    onClick={() => setChangeFilter('All')}
                  >
                    {`${stats.total} Regrades`}
                  </button>
                  <button
                    type="button"
                    className={`cracked-summary-chip ${changeFilter === 'Upgrade' ? 'active cracked-summary-chip-upgrade' : ''}`}
                    onClick={() => toggleFilter('Upgrade')}
                  >
                    {`Upgrades ${stats.upgrades}`}
                  </button>
                  <button
                    type="button"
                    className={`cracked-summary-chip ${changeFilter === 'Same' ? 'active cracked-summary-chip-same' : ''}`}
                    onClick={() => toggleFilter('Same')}
                  >
                    {`Same ${stats.same}`}
                  </button>
                  <button
                    type="button"
                    className={`cracked-summary-chip ${changeFilter === 'Downgrade' ? 'active cracked-summary-chip-downgrade' : ''}`}
                    onClick={() => toggleFilter('Downgrade')}
                  >
                    {`Downgrades ${stats.downgrades}`}
                  </button>
                  <button
                    type="button"
                    className={`cracked-summary-chip ${changeFilter === 'Unknown' ? 'active cracked-summary-chip-unknown' : ''}`}
                    onClick={() => toggleFilter('Unknown')}
                  >
                    {`Unknown ${stats.unknown}`}
                  </button>
                </div>
              </Row>
              <Row className="d-flex justify-content-center">
                <Col md={5} sm={8} xs={12}>
                  <div className="cracked-search-wrap">
                    <input
                      id="cracked-search"
                      className="cards-filter-control cards-filter-control--full"
                      placeholder="Search by card name, cert, set, grade, or ID"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="subCardWall d-flex align-items-center justify-content-center">
                {loading ? (
                  <Col>
                    <div className="custom-info-misc cracked-status">Loading regrades...</div>
                  </Col>
                ) : filteredRows.length > 0 ? (
                  filteredRows.map((item) => (
                    <Col xxl={4} xl={6} lg={6} md={12} className="imagesPageRow d-flex align-items-stretch justify-content-center" key={item.new_card_id}>
                      <PairCard item={item} />
                    </Col>
                  ))
                ) : (
                  <Col>
                    <div className="custom-info-misc cracked-status">No regrades found for that filter.</div>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default CrackedCardsPage;
